"use client";

import type {ComponentProps} from "react";
import AssistantContentParts from "@/components/ask/AssistantContentParts";
import {groupApprovalParts, isApprovalActive} from "@/utils/chat/approvalParts";
import type {ToolApprovalResponse} from "@/services/wingman";
import ApprovalReview from "./ApprovalReview";
import {useReplyApprovalDecisions} from "@/contexts/chat/ToolApprovalDecisionContext";

type Props = ComponentProps<typeof AssistantContentParts> & Omit<ComponentProps<typeof ApprovalReview>, "parts" | "batch" | "onRespond"> & {
   onRespond?: (decisions: ToolApprovalResponse[]) => Promise<boolean>;
};

/** Approval groups form visible boundaries, preserving the order of follow-up answers. */
export default function ApprovalContentParts({contentParts, conversationId, messageId, approvalMessageIds, activeMessageId, disabled, onRespond, onRequestFresh, ...props}: Props) {
   const groups = groupApprovalParts(contentParts, !!props.isUser);
   // Wingman settles a reply's pending approvals together, whichever group shows them.
   const pending = groups.flatMap(group => group.approval ? group.parts : []).filter(part =>
      !part.isCompleted && part.data.approval.approved === undefined &&
      isApprovalActive(part, {messageId, approvalMessageIds, activeMessageId}));
   const {decide, undecidedIds, submitting} = useReplyApprovalDecisions({
      approvals: pending.map(part => part.data.approval), onSubmit: onRespond,
   });
   const lastApprovalGroup = groups.findLastIndex(group => group.approval);
   return <>{groups.map((group, index) => group.approval ? <ApprovalReview
      key={group.parts[0].data.approval.id}
      parts={group.parts} conversationId={conversationId} messageId={messageId} approvalMessageIds={approvalMessageIds}
      activeMessageId={activeMessageId} disabled={disabled} onRespond={onRespond ? decide : undefined} onRequestFresh={onRequestFresh}
      batch={pending.length > 1 ? {pending, undecidedIds, submitting, inline: index === lastApprovalGroup} : undefined}
   /> : <AssistantContentParts key={`content:${group.start}`} {...props} conversationId={conversationId} contentParts={group.parts} />)}</>;
}
